/**
 * MKS-500 — the slim/full column-header toggle, in the board toolbar.
 *
 * Same control as the one MKS-378 put in the swimlane's corner cell (same
 * labels, same `aria-pressed`, same preference): only its mounting point
 * moved, so the corner cell no longer spends the board's leftmost 240px track
 * on a single small button and the task columns start further left.
 *
 * Purely presentational, like BoardSortControl / PriorityFilterToggle next to
 * it: the page owns the preference (`useSlimHeaderPreference`) and passes it
 * down, and borrows that row's control styling so the toolbar reads as one row.
 */
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

export interface SlimHeaderToggleProps {
  /** Is the slim header on right now? */
  value: boolean;
  /** Flip it. The page persists the new value. */
  onToggle: () => void;
}

export function SlimHeaderToggle({ value, onToggle }: SlimHeaderToggleProps) {
  const label = value ? 'Show the full column header' : 'Show a slim column header';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={value}
      aria-label={label}
      title={label}
      data-testid="slim-header-toggle"
      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-ms-border bg-ms-surface px-2 py-1 text-[12px] font-medium text-ms-text-secondary hover:bg-ms-canvas focus:outline-none focus:ring-1 focus:ring-ms-orange/40"
    >
      {value ? (
        <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5" />
      ) : (
        <ChevronUpIcon aria-hidden="true" className="h-3.5 w-3.5" />
      )}
      {value ? 'Full header' : 'Slim header'}
    </button>
  );
}
